import React, { useMemo } from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

/**
 * 計算字符重複率
 */
const calculateCharRepetitionRate = (content) => {
  if (!content || typeof content !== 'string' || content.length === 0) {
    return 0;
  }
  const charCount = {};
  for (let char of content) {
    charCount[char] = (charCount[char] || 0) + 1;
  }
  const totalChars = content.length;
  const uniqueChars = Object.keys(charCount).length;
  return totalChars > 0 ? ((totalChars - uniqueChars) / totalChars) * 100 : 0;
};

/**
 * 計算標準差
 */
const calculateStandardDeviation = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const avgSquaredDiff = squaredDiffs.reduce((sum, val) => sum + val, 0) / values.length;
  return Math.sqrt(avgSquaredDiff);
};

/**
 * 分析用戶發言習慣
 */
const analyzeUserSpeakingHabits = (nodes, edges = []) => {
  const userStats = {};
  const allAuthors = new Set();
  
  // 收集所有用戶
  nodes.forEach(node => {
    if (node.author) {
      allAuthors.add(node.author);
    }
  });
  
  const totalUsers = allAuthors.size;
  
  // 建立節點ID到作者的映射
  const nodeAuthorMap = {};
  nodes.forEach(node => {
    if (node.id && node.author) {
      nodeAuthorMap[node.id] = node.author;
    }
  });
  
  // 計算每個節點被多少不同用戶回應
  const nodeResponseMap = {}; // { nodeId: Set<authorId> }
  edges.forEach(edge => {
    const parentNodeId = edge.to;
    const childNodeId = edge.from;
    const childAuthor = nodeAuthorMap[childNodeId];
    
    if (parentNodeId && childAuthor) {
      if (!nodeResponseMap[parentNodeId]) {
        nodeResponseMap[parentNodeId] = new Set();
      }
      nodeResponseMap[parentNodeId].add(childAuthor);
    }
  });
  
  nodes.forEach(node => {
    const { author, content, id } = node;
    if (!author || !content || typeof content !== 'string') {
      return;
    }
    
    if (!userStats[author]) {
      userStats[author] = {
        lengths: [],
        repetitionRates: [],
        count: 0,
        nodeIds: []
      };
    }
    
    const length = content.length;
    const repetitionRate = calculateCharRepetitionRate(content);
    
    userStats[author].lengths.push(length);
    userStats[author].repetitionRates.push(repetitionRate);
    userStats[author].count++;
    userStats[author].nodeIds.push(id);
  });
  
  // 計算統計數據
  const result = {};
  Object.keys(userStats).forEach(author => {
    const stats = userStats[author];
    const lengths = stats.lengths;
    const repetitionRates = stats.repetitionRates;
    
    // 計算節點被引用率：平均有多少比例的其他用戶回應了該用戶的節點
    let totalResponseRate = 0;
    let nodesWithResponses = 0;
    
    let totalResponders = 0;
    
    stats.nodeIds.forEach(nodeId => {
      const responders = nodeResponseMap[nodeId];
      if (responders && responders.size > 0) {
        totalResponders += responders.size;
      }
    });
    
    const avgResponders = stats.nodeIds.length > 0 ? totalResponders / stats.nodeIds.length : 0;
    const avgResponseRate = totalUsers > 0 ? (avgResponders / totalUsers) * 100 : 0;
    
    result[author] = {
      avgLength: lengths.length > 0 ? lengths.reduce((sum, val) => sum + val, 0) / lengths.length : 0,
      stdDevLength: calculateStandardDeviation(lengths),
      avgRepetitionRate: repetitionRates.length > 0 ? repetitionRates.reduce((sum, val) => sum + val, 0) / repetitionRates.length : 0,
      nodeCount: stats.count,
      avgResponders: avgResponders,
      avgResponseRate: avgResponseRate
    };
  });
  
  return result;
};

/**
 * 用戶發言分析表格組件
 * 
 * @param {Array} nodes - 節點數據陣列
 * @param {Array} edges - 邊數據陣列
 */
const UserSpeakingHabitsTable = ({ nodes = [], edges = [] }) => {
  const habitsData = useMemo(() => {
    if (nodes.length === 0) return [];
    
    const habits = analyzeUserSpeakingHabits(nodes, edges);
    const authors = Object.keys(habits);
    
    return authors.map(author => ({
      author,
      avgLength: habits[author].avgLength,
      stdDevLength: habits[author].stdDevLength,
      repetitionRate: habits[author].avgRepetitionRate,
      nodeCount: habits[author].nodeCount,
      avgResponders: habits[author].avgResponders,
      avgResponseRate: habits[author].avgResponseRate
    })).sort((a, b) => b.nodeCount - a.nodeCount);
  }, [nodes, edges]);

  if (nodes.length === 0) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 2, width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
        用戶發言分析
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>用戶</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>發言次數</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>平均發言長度</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>長度標準差</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>字符重複率 (%)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>平均回覆人數</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>回覆機率 (%)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {habitsData.map((row) => (
              <TableRow
                key={row.author}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {row.author}
                </TableCell>
                <TableCell align="right">{row.nodeCount}</TableCell>
                <TableCell align="right">{row.avgLength.toFixed(2)}</TableCell>
                <TableCell align="right">{row.stdDevLength.toFixed(2)}</TableCell>
                <TableCell align="right">{row.repetitionRate.toFixed(2)}</TableCell>
                <TableCell align="right">{row.avgResponders.toFixed(2)}</TableCell>
                <TableCell align="right">{row.avgResponseRate.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default UserSpeakingHabitsTable;
